import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Header } from '@/components/Header';
import { PartnersBlogBanner } from '@/components/PartnersBlogBanner';
import { BlogShareRow } from '@/components/BlogShareRow';
import BottomNav from '@/components/BottomNav';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/context/LanguageContext';
import { apiFetch } from '@/hooks/useFetch';
import { ArrowLeft, Calendar, Eye, User, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BlogTableOfContents, useTocFromHtml } from '@/components/BlogTableOfContents';
import { NewsletterCta } from '@/components/NewsletterCta';
import { BlogReactions } from '@/components/BlogReactions';
import AdSenseUnit from '@/components/AdSenseUnit';

/**
 * Inject zero-width spaces inside long brand-name compound words so they
 * wrap cleanly on narrow viewports. Only applied to text nodes between
 * HTML tags — attribute values (href URLs, etc.) are untouched, so links
 * to clearcommunicationapp.com remain intact.
 */
const ZWSP = '\u200B';
const BRAND_WRAPS = {
  ClearCommunicationApp: `Clear${ZWSP}Communication${ZWSP}App`,
};
const injectBrandZwsp = (html) => {
  if (!html) return html;
  return html.replace(/(<[^>]+>)|([^<]+)/g, (_, tag, text) => {
    if (tag) return tag;
    let out = text;
    for (const [k, v] of Object.entries(BRAND_WRAPS)) {
      out = out.split(k).join(v);
    }
    return out;
  });
};

const BlogPostPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { t, isSpanish } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);

  const { items: tocItems, html: contentHtml } = useTocFromHtml(post?.content);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setRelated([]);
      const langQ = isSpanish ? '?lang=es' : '';
      const { data } = await apiFetch(`/api/blog/posts/${postId}${langQ}`);
      if (data) {
        // Canonical URL enforcement: if the post was reached via its
        // internal post_id or a retired slug (URL was renamed), swap
        // the address bar to the current permalink.
        if (data.slug && postId !== data.slug) {
          navigate(`/blog/${data.slug}`, { replace: true });
          return;
        }
        setPost(data);
        try {
          const { track } = await import('@/lib/analytics');
          track.blogView(data.slug || postId, data.category);
        } catch { /* ignore */ }
      }
      setLoading(false);
      // Fetch related posts in parallel after main post
      const relQ = isSpanish ? '&lang=es' : '';
      const { data: relData } = await apiFetch(`/api/blog/posts/${postId}/related?limit=3${relQ}`);
      if (relData?.related) setRelated(relData.related);
    })();
    // Scroll to top whenever post changes
    window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
  }, [postId, isSpanish]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex justify-center py-20">
          <div className="flex gap-1">
            <div className="w-2 h-2 rounded-full bg-[#00E5CC] animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 rounded-full bg-[#00E5CC] animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 rounded-full bg-[#00E5CC] animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container py-20 text-center">
          <p className="text-xl text-muted-foreground mb-4">Post not found</p>
          <Button onClick={() => navigate('/blog')} className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d]">
            <ArrowLeft className="h-4 w-4 mr-2" /> {t('backToBlog')}
          </Button>
        </div>
      </div>
    );
  }

  // Resolve the post's social share image. We always point socials at the
  // dynamic branded OG card (title + author + cover thumb composited on
  // the brand background) rather than the raw cover image. This gives
  // every share — Facebook, Threads, LinkedIn, X, Slack, Discord,
  // iMessage — the same on-brand, click-worthy preview. The endpoint
  // Social preview image — when the post has a cover image we share
  // the cover directly so it looks like the rest of the post; otherwise
  // we fall back to the auto-generated branded /api/og/blog/{slug}.png
  // card that composites title + Dino mascot for posts without a cover.
  const _slug = post.slug || post.post_id;
  const _coverAbs = post.cover_image
    ? (post.cover_image.startsWith('http')
        ? post.cover_image
        : `https://clearcommunicationapp.com${post.cover_image}`)
    : null;
  const shareImageUrl = _coverAbs || `https://clearcommunicationapp.com/api/og/blog/${_slug}.png`;
  const shareImageType = _coverAbs && /\.png(\?|$)/i.test(_coverAbs) ? 'image/png' : 'image/jpeg';

  return (
    <div className="min-h-screen bg-background pb-24">
      <Helmet>
        <title>{post.title} | ClearCommunicationApp</title>
        <meta name="description" content={post.excerpt || post.title} />
        <link rel="canonical" href={`https://clearcommunicationapp.com/blog/${_slug}`} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="ClearCommunicationApp" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || post.title} />
        <meta property="og:url" content={`https://clearcommunicationapp.com/blog/${_slug}`} />
        <meta property="og:image" content={shareImageUrl} />
        <meta property="og:image:secure_url" content={shareImageUrl} />
        <meta property="og:image:type" content={shareImageType} />
        {!_coverAbs && <meta property="og:image:width" content="1200" />}
        {!_coverAbs && <meta property="og:image:height" content="630" />}
        <meta property="og:image:alt" content={post.title} />
        {post.published_at && <meta property="article:published_time" content={post.published_at} />}
        {post.author_name && <meta property="article:author" content={post.author_name} />}
        {(post.tags || []).map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}

        {/* Twitter / X */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt || post.title} />
        <meta name="twitter:image" content={shareImageUrl} />
        <meta name="twitter:image:alt" content={post.title} />

        {/* JSON-LD Article schema for Google rich results / Top Stories eligibility.
            Must be on a separate <script type="application/ld+json"> tag. */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt || post.title,
            image: [
              post.cover_image,
              `https://clearcommunicationapp.com/api/og/blog/${post.slug || post.post_id}.png`,
            ].filter(Boolean),
            datePublished: post.published_at || post.created_at || undefined,
            dateModified: post.updated_at || post.published_at || post.created_at || undefined,
            author: {
              "@type": "Person",
              name: post.author_name || "ClearCommunicationApp",
            },
            publisher: {
              "@type": "Organization",
              name: "ClearCommunicationApp",
              logo: {
                "@type": "ImageObject",
                url: "https://clearcommunicationapp.com/icon-pixel-512.png",
              },
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `https://clearcommunicationapp.com/blog/${post.slug || post.post_id}`,
            },
            keywords: (post.tags || []).join(", ") || undefined,
            articleSection: post.category || undefined,
          })}
        </script>

        {/* BreadcrumbList JSON-LD — Google often shows breadcrumbs in SERPs
            instead of the raw URL, which looks cleaner and improves CTR. */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://clearcommunicationapp.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Blog",
                item: "https://clearcommunicationapp.com/blog",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: post.title,
                item: `https://clearcommunicationapp.com/blog/${post.slug || post.post_id}`,
              },
            ],
          })}
        </script>
      </Helmet>
      <Header />
      <article className="container py-6 sm:py-10">
        <div className="max-w-3xl mx-auto">
          {/* Visible breadcrumb (also indexed via BreadcrumbList JSON-LD above) */}
          <nav aria-label="Breadcrumb" className="mb-4 text-sm text-muted-foreground" data-testid="blog-breadcrumb">
            <ol className="flex items-center flex-wrap gap-1.5">
              <li>
                <Link to="/" className="hover:text-foreground hover:underline transition-colors">
                  Home
                </Link>
              </li>
              <li aria-hidden="true">
                <ChevronRight className="h-3.5 w-3.5" />
              </li>
              <li>
                <Link to="/blog" className="hover:text-foreground hover:underline transition-colors">
                  Blog
                </Link>
              </li>
              <li aria-hidden="true">
                <ChevronRight className="h-3.5 w-3.5" />
              </li>
              <li className="text-foreground font-medium truncate max-w-[60vw] sm:max-w-md" aria-current="page">
                {post.title}
              </li>
            </ol>
          </nav>

          <Button
            onClick={() => navigate('/blog')}
            className="mb-6 bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d]"
            size="sm"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('backToBlog')}
          </Button>

          {post.cover_image && (
            <div className="aspect-video rounded-lg overflow-hidden mb-6">
              <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
            </div>
          )}

          {post.category && (
            <Badge variant="secondary" className="mb-3">{post.category}</Badge>
          )}

          <h1 className="text-2xl sm:text-4xl font-bold mb-4" data-testid="blog-post-title">
            {(post.title || '').split('ClearCommunicationApp').reduce((acc, part, i) => {
              if (i === 0) return [part];
              return [...acc, <React.Fragment key={i}>Clear{ZWSP}Communication{ZWSP}App</React.Fragment>, part];
            }, [])}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6 pb-6 border-b">
            <div className="flex items-center gap-1">
              <User className="h-4 w-4" />
              {post.author_name ? (
                <Link
                  to={`/author/${post.author_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                  className="hover:text-[#00E5CC] hover:underline transition-colors"
                  data-testid="blog-author-link"
                >
                  {post.author_name}
                </Link>
              ) : 'ClearCommunicationApp'}
            </div>
            <div className="flex items-center gap-1" title={post.updated_at ? `Updated ${new Date(post.updated_at).toLocaleString()}` : ''}>
              <Calendar className="h-4 w-4" />
              {(() => {
                // Always show a date — fall back through published_at →
                // updated_at → created_at so the byline is never empty
                // (helps E-E-A-T signals for Google).
                const raw = post.published_at || post.updated_at || post.created_at;
                if (!raw) return '';
                const label = new Date(raw).toLocaleDateString(isSpanish ? 'es-ES' : 'en-US', {
                  year: 'numeric', month: 'long', day: 'numeric'
                });
                return isSpanish ? `Publicado el ${label}` : `Posted on ${label}`;
              })()}
            </div>
            <div className="flex items-center gap-1">
              <Eye className="h-4 w-4" />
              {post.views || 0} views
            </div>
          </div>

          {/* Blog content - rendered from HTML.
              Wrapping rules (May 2026 — final fix after user-reported
              mid-syllable splits):
              • <pre> / <code> / <a> use `break-words` because they
                contain tokens that may have no whitespace (long URLs,
                identifiers, code blocks).
              • Body text (p / li / headings) uses
                `overflow-wrap:break-word` — wraps only at unbreakable
                tokens, never mid-word. Combined with
                `word-break:normal` and `hyphens:manual` so the browser
                doesn't introduce its own mid-word hyphens. */}
          <BlogTableOfContents items={tocItems} />
          <div
            className="prose prose-lg dark:prose-invert max-w-none
              prose-headings:text-foreground prose-p:text-foreground/90
              prose-a:text-[#00E5CC] prose-strong:text-foreground
              prose-img:rounded-lg
              prose-h1:text-2xl sm:prose-h1:text-3xl md:prose-h1:text-5xl
              prose-h2:text-xl sm:prose-h2:text-2xl md:prose-h2:text-4xl
              prose-h3:text-lg sm:prose-h3:text-xl md:prose-h3:text-3xl
              [&_pre]:whitespace-pre-wrap [&_pre]:break-words
              [&_code]:whitespace-pre-wrap [&_code]:break-words
              [&_a]:break-words
              [&_p]:[overflow-wrap:break-word] [&_li]:[overflow-wrap:break-word]
              [&_h1]:[overflow-wrap:break-word] [&_h2]:[overflow-wrap:break-word] [&_h3]:[overflow-wrap:break-word]
              [&_p]:[word-break:normal] [&_li]:[word-break:normal]
              [&_h1]:[word-break:normal] [&_h2]:[word-break:normal] [&_h3]:[word-break:normal]
              [&_p]:[hyphens:manual] [&_li]:[hyphens:manual]
              [&_h1]:[hyphens:manual] [&_h2]:[hyphens:manual] [&_h3]:[hyphens:manual]
              [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto
              [&_table]:border-collapse [&_table]:my-6 [&_table]:text-sm
              [&_th]:border [&_th]:border-slate-300 dark:[&_th]:border-slate-700
              [&_th]:bg-slate-100 dark:[&_th]:bg-slate-800
              [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold
              [&_td]:border [&_td]:border-slate-200 dark:[&_td]:border-slate-700
              [&_td]:px-3 [&_td]:py-2 [&_td]:align-top
              [&_tr:nth-child(even)_td]:bg-slate-50/60 dark:[&_tr:nth-child(even)_td]:bg-slate-900/40"
            dangerouslySetInnerHTML={{ __html: injectBrandZwsp(contentHtml) }}
            data-testid="blog-post-content"
          />

          {/* In-article AdSense slot — only renders for free / guest users. */}
          <AdSenseUnit type="in-article" />

          {post.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t">
              {post.tags.map(tag => (
                <Badge
                  key={tag}
                  variant="outline"
                  className="cursor-pointer hover:bg-[#00E5CC]/10"
                  onClick={() => navigate(`/blog?tag=${encodeURIComponent(tag)}`)}
                >
                  #{tag}
                </Badge>
              ))}
            </div>
          )}

          {/* Author micro-bio — small "about the author" block right
              under the article body so readers know who wrote it before
              they're asked to react. Uses the contributor's public author
              fields (display name, photo, credentials, bio) when present,
              and gracefully falls back to first letter + post.author_name. */}
          {post.author_name && (
            <aside
              className="mt-10 rounded-xl border border-border bg-muted/30 p-4 sm:p-5 flex items-start gap-4"
              data-testid="blog-author-microbio"
            >
              {post.author_photo ? (
                <img
                  src={post.author_photo}
                  alt={post.author_display_name || post.author_name}
                  className="shrink-0 w-14 h-14 rounded-full object-cover border-2 border-[#00E5CC]"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <div className="shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#00E5CC] to-[#00A896] flex items-center justify-center text-[#0d1f2d] font-bold text-lg">
                  {(post.author_display_name || post.author_name || '?').charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-0.5">
                  {isSpanish ? 'Sobre el autor' : 'About the author'}
                </p>
                <Link
                  to={`/author/${(post.author_name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                  className="font-semibold text-base text-foreground hover:text-[#00E5CC] transition-colors"
                  data-testid="blog-author-microbio-name"
                >
                  {post.author_display_name || post.author_name}
                </Link>
                {post.author_credentials && (
                  <p className="text-xs text-muted-foreground mt-0.5" data-testid="blog-author-credentials">
                    {post.author_credentials}
                  </p>
                )}
                {post.author_bio ? (
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-3">
                    {post.author_bio}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">
                    {isSpanish
                      ? 'Contribuyendo a ClearCommunicationApp para ayudar a cerrar las brechas de comunicación.'
                      : 'Contributing to ClearCommunicationApp to help bridge communication gaps.'}
                  </p>
                )}
              </div>
            </aside>
          )}

          {/* Related articles — placed BEFORE the "was this helpful"
              reactions block so readers see follow-up reading options
              while their attention is still on the content topic. */}
          {related.length > 0 && (
            <section className="mt-12 pt-8 border-t" data-testid="related-articles-section">
              <h2 className="text-xl sm:text-2xl font-bold mb-5">{t('relatedArticles')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {related.map(r => (
                  <button
                    key={r.post_id || r.slug}
                    onClick={() => navigate(`/blog/${r.slug || r.post_id}`)}
                    className="text-left group rounded-xl border bg-card hover:border-[#00E5CC] hover:shadow-lg transition-all overflow-hidden p-4 flex flex-col h-full"
                    data-testid={`related-article-${r.slug || r.post_id}`}
                  >
                    {r.category && (
                      <Badge variant="secondary" className="mb-2 w-fit text-[10px]">{r.category}</Badge>
                    )}
                    <h3 className="font-semibold text-base leading-snug group-hover:text-[#00E5CC] transition-colors line-clamp-2 mb-2">
                      {r.title}
                    </h3>
                    {r.excerpt && (
                      <p className="text-xs text-muted-foreground line-clamp-3 mb-3">
                        {r.excerpt}
                      </p>
                    )}
                    <div className="mt-auto flex items-center gap-3 text-[11px] text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {r.published_at ? new Date(r.published_at).toLocaleDateString('en-US', {
                          year: 'numeric', month: 'short', day: 'numeric'
                        }) : ''}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[#00E5CC] font-medium group-hover:gap-2 transition-all">
                        {t('readMore')} →
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Reader reactions — shows up directly under the article so
              readers can react before being asked to subscribe / share. */}
          <BlogReactions postId={post.slug || post.post_id} isSpanish={isSpanish} />

          {/* Newsletter signup CTA — appears after the article content,
              before share buttons. High-conversion zone since reader has
              just finished a full piece. */}
          <NewsletterCta source={`blog:${post.slug || post.post_id}`} />

          {/* Share buttons — show pre-filled X / Facebook / LinkedIn / Threads
              intents plus copy-link & native share for Instagram / mobile. */}
          <BlogShareRow post={post} />

          {/* Conversion banner — autism orgs / schools / therapy practices */}
          <PartnersBlogBanner />
        </div>
      </article>
      <BottomNav />
    </div>
  );
};

export default BlogPostPage;
